// Mirror payment certificates to the Atelier project they belong to (via this
// worker, which holds the shared key). Atelier shows them in the studio cockpit
// and the client portal, and the client is sent to them from there.
import type { Contract, PaymentCert, PaymentProject } from "../payments/types";
import { certSeries, CERT_SERIES_LABEL } from "../payments/types";
import { fileUrl } from "./filesApi";

export interface AtelierProjectRef { id: string; code: string; name: string; clientEmail?: string }

/** The certificate as Atelier stores it — a flat, link-bearing record. */
export interface AtelierCertRecord {
  id: string;
  number: string;
  series: string;
  title: string;
  contractName: string;
  payee: string;
  amount: number;
  currency: string;
  date: string;
  status: string;
  description?: string;
  shareUrl?: string;
  invoice?: { name: string; url: string; size?: number };
  emails?: { to: string[]; cc?: string[]; subject: string; at: string }[];
  receiptConfirmedBy?: string;
  updatedAt: string;
  source: "contract-gen";
}

export function certRecordFor(project: PaymentProject, contract: Contract, cert: PaymentCert): AtelierCertRecord {
  const origin = typeof location !== "undefined" ? location.origin : "https://contracts.studiogy.design";
  const series = certSeries(cert);
  return {
    id: cert.id,
    number: cert.number,
    series,
    title: CERT_SERIES_LABEL[series],
    contractName: contract.name,
    payee: contract.party,
    amount: isFinite(cert.amount) ? cert.amount : 0,
    currency: cert.currency || contract.currency || project.currency,
    date: cert.date,
    status: cert.status,
    description: cert.description,
    shareUrl: cert.shareId ? `${origin}/?review=${cert.shareId}` : undefined,
    invoice: cert.invoice ? { name: cert.invoice.name, url: `${origin}${fileUrl(cert.invoice.id)}`, size: cert.invoice.size } : undefined,
    emails: (cert.emailLog || []).map((e) => ({ to: e.to, cc: e.cc, subject: e.subject, at: e.at })),
    receiptConfirmedBy: cert.receipt?.confirmedBy,
    updatedAt: new Date().toISOString(),
    source: "contract-gen",
  };
}

export async function listAtelierProjects(): Promise<{ configured: boolean; projects: AtelierProjectRef[]; error?: string }> {
  const r = await fetch("/api/atelier/projects");
  const d: any = await r.json().catch(() => ({}));
  if (r.status === 401) return { configured: true, projects: [], error: "Sign in to link Atelier." };
  return { configured: !!d.configured, projects: d.projects || [], error: d.error };
}

export async function pushCertificate(atelierProjectId: string, certificate: AtelierCertRecord): Promise<{ ok: boolean; portalUrl?: string; projectName?: string }> {
  const r = await fetch("/api/atelier/certificate", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ atelierProjectId, certificate }) });
  const d: any = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d?.error || `HTTP ${r.status}`);
  return d;
}
